import type { ApiResponse } from "@internal/backstage-plugin-utility-common";

import type { RepoRelease } from "../../types";
import {
    GitHubBaseClient,
    type GitHubClientRequestOptions,
} from "../GitHubBaseClient";
import type { GitHubGraphQLClient } from "../GitHubGraphQLClient";
import type { GitHubRepoClient } from "../GitHubRepoClient";

/**
 * Per-request options for {@link GitHubClient.getRepoReleases}.
 *
 * @public
 */
export interface GetRepoReleasesOptions extends GitHubClientRequestOptions {
    /**
     * Maximum number of releases to return, newest first.
     * Defaults to the backend's default (5) when omitted. Maximum is 100.
     */
    perPage?: number;
}

/**
 * Client for the GitHub backend plugin.
 *
 * Sub-clients are mounted automatically at construction time by `ClientRegistry`
 * as each module's `-common` package is evaluated. The `graphql` and `repo`
 * sub-clients are registered by `github-common` itself via `module.ts`.
 *
 * @public
 */
export class GitHubClient extends GitHubBaseClient {
    /** Sub-client for GitHub GraphQL-backed endpoints. */
    declare readonly graphql: GitHubGraphQLClient;

    /** Sub-client for GitHub repository content and pull-request endpoints. */
    declare readonly repo: GitHubRepoClient;

    /**
     * Fetches the most recent releases for a repository, newest first.
     *
     * @param owner - The GitHub org/user that owns the repo (e.g., "Banno")
     * @param repo - The repository name (e.g., "backstage")
     * @param options - Optional request options (`perPage`, and `githubToken` for user-scoped calls)
     * @returns ApiResponse with an array of releases, newest first
     */
    getRepoReleases(
        owner: string,
        repo: string,
        options?: GetRepoReleasesOptions
    ): Promise<ApiResponse<RepoRelease[]>> {
        const query = options?.perPage
            ? `?per_page=${encodeURIComponent(options.perPage)}`
            : "";

        return this.api<RepoRelease[]>(
            `repos/${owner}/${repo}/releases${query}`,
            { method: "GET", headers: this.buildGitHubHeaders(options) }
        );
    }
}
